import { CHILD_BENEFIT_TYPES } from 'cpool-constants';
import type { ApplicantCreation } from 'cpool-schemas';
import { t } from 'i18next';
import { toast } from 'react-toastify';

import { createApplicantRoutine } from './routine/createApplicantRoutine';
import { setQueryPosition } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys';

type Props = {
    setSubmitting: (submitting: boolean) => void;
    aggregateId: string;
    onClose: () => void;
    reset: () => void;
};

export const onSubmit =
    ({ aggregateId, onClose, reset, setSubmitting }: Props) =>
    async (data: ApplicantCreation) => {
        setSubmitting(true);

        const adjustedData: ApplicantCreation = {
            ...data,
            kinder: data.kinder?.map((child) => ({
                ...child,
                kindergeldFuer: child.kindergeldHoehe ? CHILD_BENEFIT_TYPES.ERSTES_ODER_ZWEITES_KIND : null,
            })),
        };

        const { parsingErrors, position, requestErrors } = await createApplicantRoutine({
            aggregateId,
            data: adjustedData,
        });

        if (parsingErrors && parsingErrors.length > 0) {
            const failedCommands = parsingErrors.map((error) => error.commandName).join(', ');
            toast.error(t('LEAD_DETAILS_GENERAL.APPLICANT_CREATION_PARSING_ERROR', { error: failedCommands }));
        }

        if (requestErrors) {
            const isOnlyCreateApplicantError =
                requestErrors.length === 1 && requestErrors[0].commandName === 'createApplicant';

            if (isOnlyCreateApplicantError) {
                toast.error(t('LEAD_DETAILS_GENERAL.APPLICANT_CREATION_FAILED'));
            } else {
                const failedCommands = requestErrors.map((error) => error.commandName).join(', ');
                toast.warning(
                    t('LEAD_DETAILS_GENERAL.APPLICANT_CREATION_PARTIAL_FAILURE', { actions: failedCommands }),
                );
            }
        }

        if (position) {
            const getKey = queryKeys.get(aggregateId);
            const getDemandedDocumentsKey = queryKeys.getDemandedDocuments(aggregateId);
            const getRecordedDocumentsKey = queryKeys.getRecordedDocuments(aggregateId);
            const getOfferRequestConditionsLatestKey = queryKeys.getOfferRequestConditionsLatest(aggregateId);

            setQueryPosition(JSON.stringify(getKey), position);
            setQueryPosition(JSON.stringify(getDemandedDocumentsKey), position);
            setQueryPosition(JSON.stringify(getRecordedDocumentsKey), position);
            setQueryPosition(JSON.stringify(getOfferRequestConditionsLatestKey), position);

            if (!requestErrors && !parsingErrors) {
                toast.success(t('LEAD_DETAILS_GENERAL.APPLICANT_CREATED'));
                reset();
                onClose();
            }
        }

        setSubmitting(false);
    };
